import { Card, CardContent, Grid } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import i18n from 'simple-react-i18n'
import { groupBy, indexOf, keys, maxBy, template, values } from 'lodash'
import { IAEAU_SCALE_COLORS, INDICATORS_COLORS } from 'utils/constants/ColorTheme'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import { CONFORMITY_COLORS } from 'quality/utils/conformityExportUtils'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { DEFAULT_CONTRIBUTOR_TYPE } from 'administration/components/user/constants/UserConstants'
import moment from 'moment'
import { COMPLIANT, LIMIT_EXCEEDED, NO_RECENT_OPERATION, REFERENCE_EXCEEDED } from 'station/constants/ComplianceConstants'
import ProgressCard from 'components/card/ProgressCard'
import { generateGradient } from 'utils/ColorUtil'
import { shallowEqual, useSelector } from 'react-redux'

const buildOptions = (data) => {
    const formatTooltip = ({ marker, data: { name = '' } }) => `${marker} ${name}`
    return {
        tooltip: {
            trigger: 'item',
            formatter: formatTooltip,
        },
        legend: {
            orient: 'vertical',
            top: 'center',
            right: '10%',
            itemWidth: 28,
            itemHeight: 17,
            textStyle: {
                width: 300,
                overflow: 'break',
                fontSize: 13,
                padding: [0, 0, 0, 10],
            },
        },
        series: [
            {
                type: 'pie',
                radius: ['35%', '80%'],
                label: {
                    show: false,
                },
                data,
                right: '60%',
            },
        ],
    }
}

const sortDistributionUnitByCompliance = ({ compliance }) => {
    switch (compliance) {
        case LIMIT_EXCEEDED: return i18n.nonConform
        case REFERENCE_EXCEEDED: return i18n.referenceExceeded
        case COMPLIANT: return i18n.compliant
        case NO_RECENT_OPERATION: return i18n.noData
        default: return i18n.notDetermined
    }
}

const getIndicatorColor = (key) => {
    switch (key) {
        case i18n.nonConform : return CONFORMITY_COLORS.nonConform
        case i18n.referenceExceeded : return CONFORMITY_COLORS.referenceExceeded
        case i18n.compliant : return CONFORMITY_COLORS.conform
        case i18n.noData : return INDICATORS_COLORS.GREY
        default: return INDICATORS_COLORS.BLACK
    }
}

const orderIndicator = (key) => {
    switch (key) {
        case i18n.nonConform : return 3
        case i18n.referenceExceeded : return 2
        case i18n.compliant: return 1
        default: return 4
    }
}

const DistributionUnitsComplianceChart = ({
    units = [],
}) => {
    const groupedIndicators = groupBy(units, sortDistributionUnitByCompliance)

    const formattedData = keys(groupedIndicators).sort((a, b) => orderIndicator(a) > orderIndicator(b) ? 1 : -1).map(key => {
        const data = groupedIndicators[key]
        return {
            value: data.length,
            name: `${key} (${data.length})`,
            itemStyle: {
                color: getIndicatorColor(key),
            },
        }
    })

    return (
        <ReactECharts
            echarts={echarts}
            option={buildOptions(formattedData)}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: '150px' }}
        />
    )
}

DistributionUnitsComplianceChart.propTypes = {
    units: PropTypes.arrayOf(PropTypes.shape({})),
}

const DistributionUnitExploitationChart = ({
    units = [],
}) => {
    const expModeList = useSandreList(SANDRE.MODE_EXPLOITATION)
    const expModeColor = generateGradient(IAEAU_SCALE_COLORS.EXCELLENT, IAEAU_SCALE_COLORS.MEDIOCRE, expModeList.length)
    const groupedIndicators = groupBy(units, 'exploitationModeCode')

    const formattedData = keys(groupedIndicators).map(key => {
        const data = groupedIndicators[key]
        const exploitationMode = expModeList.find(e => e.mnemonique === key)
        return {
            value: data.length,
            name: `${exploitationMode?.name || i18n.undefined} (${data.length})`,
            itemStyle: {
                color: expModeColor[indexOf(expModeList, exploitationMode)] || INDICATORS_COLORS.GREY,
            },
        }
    })

    const option = buildOptions(formattedData)

    return (
        <ReactECharts
            echarts={echarts}
            option={option}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: '150px', maxWidth: '500px' }}
        />
    )
}

DistributionUnitExploitationChart.propTypes = {
    units: PropTypes.arrayOf(PropTypes.shape({})),
}

const DistributionUnitsKeyFigures = ({
    units = [],
}) => {
    const {
        contributors,
        contributorLinks,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        contributorLinks: store.StationReducer.contributorLinks,
    }), shallowEqual)

    const now = moment().valueOf()
    const contributorsList = useSandreList(SANDRE.CONTRIBUTOR_TYPES)
    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const valuableContributors = contributorLinks.filter(c =>
        (c.endDate >= now || !c.endDate) &&
        units.some(u => u?.id === c.idStation) &&
        contributors.find(cr => c.idContributor === cr.id),
    ).map(c => ({ ...c, startDate: c.startDate ?? 0 }))

    const finalReferents = useMemo(() => {
        const referentsList = valuableContributors.filter(c => c.contributorType === referentType)
        const referentsGroupByStation = groupBy(referentsList, 'idStation')
        return values(keys(referentsGroupByStation).map(unit => maxBy(referentsGroupByStation[unit], 'startDate')))
    }, [valuableContributors, referentType])

    const finalOperators = useMemo(() => {
        const operatorsList = valuableContributors.filter(c => c.contributorType === operatorType)
        const operatorsGroupByStation = groupBy(operatorsList, 'idStation')
        return values(keys(operatorsGroupByStation).map(unit => maxBy(operatorsGroupByStation[unit], 'startDate')))
    }, [valuableContributors, operatorType])

    return (
        <Grid container alignItems='center' sx={{ height: '100%', fontWeight: '600' }}>
            <Grid size={10}>
                {i18n.distributionUnitsName}
            </Grid>
            <Grid size={2}>
                {units.length}
            </Grid>
            <Grid size={10}>
                {template(i18n.currentContributors)({ contributor: contributorsList.find(c => c.code === referentType)?.name })}
            </Grid>
            <Grid size={2}>
                {finalReferents.length}
            </Grid>
            <Grid size={10}>
                {template(i18n.currentContributors)({ contributor: contributorsList.find(c => c.code === operatorType)?.name })}
            </Grid>
            <Grid size={2}>
                {finalOperators.length}
            </Grid>
        </Grid>
    )
}

DistributionUnitsKeyFigures.propTypes = {
    units: PropTypes.arrayOf(PropTypes.shape({})),
}

const DistributionUnitIndicators = ({
    units = [],
    complianceProgress = 100,
}) => {
    const dispatchScreen = window.innerWidth >= 1280 ? 0 : 1
    const displayComplianceChart = complianceProgress !== -1
    return (
        <Card>
            <CardContent>
                <Grid container spacing={2}>
                    {displayComplianceChart && (
                        <Grid size={5 + dispatchScreen}>
                            <StyledFieldSet sx={{ maxWidth: '500px', margin: 0, padding: '0 0 0 1rem' }}>
                                <StyledLegend>{i18n.conformity}</StyledLegend>
                                {complianceProgress === 100 && (
                                    <DistributionUnitsComplianceChart units={units} />
                                ) || (
                                    <ProgressCard
                                        progress={complianceProgress}
                                        message={i18n.loadingCompliance}
                                    />
                                )}
                            </StyledFieldSet>
                        </Grid>
                    )}
                    <Grid size={5 + (!displayComplianceChart ? 1 : dispatchScreen)} offset={displayComplianceChart ? 0 : 1}>
                        <StyledFieldSet sx={{ maxWidth: '500px', margin: 0, padding: '0 0 0 1rem' }}>
                            <StyledLegend>{i18n.exploitationMode}</StyledLegend>
                            <DistributionUnitExploitationChart units={units} />
                        </StyledFieldSet>
                    </Grid>
                    {(dispatchScreen === 0 || !displayComplianceChart) && (
                        <Grid size={2 + (!displayComplianceChart ? 1 : 0)} offset={displayComplianceChart ? 0 : 1}>
                            <StyledFieldSet sx={{ height: '100%', margin: 0, padding: '0 0 0 1rem' }}>
                                <StyledLegend>{i18n.keyFigures}</StyledLegend>
                                <DistributionUnitsKeyFigures units={units} />
                            </StyledFieldSet>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}

DistributionUnitIndicators.propTypes = {
    units: PropTypes.arrayOf(PropTypes.shape({})),
    complianceProgress: PropTypes.number,
}

export default DistributionUnitIndicators